import { EntityProcessingErrorsPanel } from '@agilelab/plugin-wb-builder-catalog';
import { WitboostSystem } from '@agilelab/plugin-wb-builder-common';
import {
  CollapseButton,
  createRefreshEntityFunction,
  customAlertApiRef,
  CustomView,
  useRefreshPermission,
  WbCard,
  WbButton,
  WbCardContent,
  WbDockableGroup,
  WbDockablePanel,
  WbDockableResizeHandle,
  WbDrawer,
  WbCardActions,
  WbDrawerContent,
} from '@agilelab/plugin-wb-platform';
import { practiceShaperImportPermission } from '@agilelab/plugin-wb-rbac-common';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef, EntityProvider } from '@backstage/plugin-catalog-react';
import { usePermission } from '@backstage/plugin-permission-react';
import { makeStyles } from '@material-ui/core';
import { WbTooltip } from '@agilelab/plugin-wb-platform';
import CachedIcon from '@material-ui/icons/Cached';
import { useState } from 'react';
import 'reactflow/dist/style.css';
import { usePracticeShaperGraph } from '../../context/PracticeShaperGraphContext';
import { Legend } from './Legend';
import { PracticesShaperGraph } from './PracticeShaperGraph';
import { TeamRolesSettingsCard } from './TeamRolesSettingsCard';
import { hasOneOfTeamRoles } from '@agilelab/plugin-wb-practice-shaper-common';

const PAGE_HEIGHT_MINUS_CONTENT = 96 + 40 + 24;

const useStyles = makeStyles(
  theme => ({
    root: {
      marginBottom: theme.spacing(2),
    },
  }),
  { name: 'PracticeShaperOverview' },
);

export function PracticeShaperOverview({
  importPageRef,
}: {
  importPageRef: string;
}) {
  const {
    selectedEntity,
    setSelectedEntity,
    data: { refetch, isFetching },
  } = usePracticeShaperGraph();
  const catalogApi = useApi(catalogApiRef);
  const alertApi = useApi(customAlertApiRef);

  const { allowed } = usePermission({
    permission: practiceShaperImportPermission,
  });

  const { allowRefresh } = useRefreshPermission(selectedEntity);

  const refreshEntity = createRefreshEntityFunction({
    entity: selectedEntity,
    catalogApi,
    alertApi,
  });

  const [legendCollapse, setLegendCollapse] = useState(true);

  const entityName = selectedEntity
    ? (selectedEntity.metadata as any).displayName ||
      selectedEntity.metadata.name
    : '';

  const classes = useStyles();

  return (
    <>
      <WbDockableGroup
        className={classes.root}
        direction="horizontal"
        style={{ height: `calc(100vh - ${PAGE_HEIGHT_MINUS_CONTENT}px)` }}
      >
        <WbDockablePanel
          collapsible
          defaultSize={30}
          tab={{
            isCollapsed: legendCollapse,
            setCollapsed: isCollapsed => setLegendCollapse(isCollapsed),
            title: 'Legend',
            direction: 'horizontal',
          }}
        >
          <WbCard
            title="Legend"
            leading={<CollapseButton onClick={() => setLegendCollapse(true)} />}
            cardStyle={{ height: '100%' }}
          >
            <WbCardContent style={{ height: '100%', overflowY: 'auto' }}>
              <Legend />
            </WbCardContent>
          </WbCard>
        </WbDockablePanel>
        <WbDockableResizeHandle direction="horizontal" />
        <WbDockablePanel defaultSize={70} collapsible>
          <WbCard
            title="Design"
            cardStyle={{ height: '100%' }}
            actions={
              <WbCardActions breakpoint="w_xs">
                <WbTooltip title="Refresh the Practice Shaper Entities">
                  <span>
                    <WbButton
                      variant="outlined"
                      onClick={() => refetch()}
                      label="Refresh"
                      icon={<CachedIcon />}
                      disabled={isFetching}
                      fullWidthUnderBreakpoint="w_xs"
                    />
                  </span>
                </WbTooltip>
                {allowed ? (
                  <WbTooltip title="Register a new Practice Shaper entity">
                    <span>
                      <WbButton
                        label="Register new entity"
                        to={importPageRef}
                        fullWidthUnderBreakpoint="w_xs"
                      />
                    </span>
                  </WbTooltip>
                ) : undefined}
              </WbCardActions>
            }
          >
            <WbCardContent
              style={{
                height: '100%',
              }}
            >
              <PracticesShaperGraph />
            </WbCardContent>
          </WbCard>
        </WbDockablePanel>
      </WbDockableGroup>
      <EntityProvider entity={selectedEntity ? selectedEntity : undefined}>
        <WbDrawer
          open={!!selectedEntity}
          setOpen={() => setSelectedEntity(null)}
          title={entityName}
        >
          {selectedEntity && (
            <WbDrawerContent
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
              }}
            >
              <EntityProcessingErrorsPanel />
              <CustomView
                id="practice_shaper_content"
                transform="builder_system"
                typeId={selectedEntity.kind}
                data={{
                  entity: selectedEntity as WitboostSystem,
                  allowRefresh,
                }}
                actions={{ refreshEntity: refreshEntity }}
              />
              {hasOneOfTeamRoles(selectedEntity) && (
                <TeamRolesSettingsCard systemType={selectedEntity} />
              )}
            </WbDrawerContent>
          )}
        </WbDrawer>
      </EntityProvider>
    </>
  );
}
